export enum RegEdFieldType {
	String = 'String',
	Text = 'Text',
	Integer = 'Integer',
	Decimal = 'Decimal',
	Boolean = 'Boolean',
	Money = 'Money',
	Date = 'Date',
	DateTime = 'DateTime',
	Time = 'Time',
	Email = 'Email',
	User = 'User',
	Contact = 'Contact',
	Option = 'Option',
	MultipleOptions = 'MultipleOptions',
	FullAddress = 'FullAddress',
	Note = 'Note',
	MeetingScheduler = 'MeetingScheduler',
	FileUpload = 'FileUpload',
	Label = 'Label',
}

export interface IRegEdMetadataOption {
	label?: string;
	externalId?: string;
	isActive?: boolean;
}

export interface IRegEdMetadataField {
	id?: string;
	externalId?: string;
	label?: string;
	helpText?: string;
	fieldType?: RegEdFieldType;
	isRequired?: boolean;
	isActive?: boolean;
	options?: IRegEdMetadataOption[];
	/** The polymorphic `_type`-discriminated `QuestionVisibility`; absent when the field is always shown. */
	visibleWhen?: Record<string, unknown> | null;
	order?: number;
	/**
	 * `yyyy-MM-dd`, set only on the requested-review-date question and computed from the advisor's own
	 * calendar day. A date on or after it cannot be rejected for lead time. Null means no window —
	 * don't infer a default.
	 */
	minimumDate?: string | null;
}

export interface IRegEdMetadataForm {
	format?: string;
	displayName?: string;
	fields?: IRegEdMetadataField[];
}

export interface IRegEdMetadataFormImportResult {
	formsWritten?: number;
	formsFailed?: number;
	questionsImported?: number;
	optionsImported?: number;
	warnings?: string[];
}

export interface IRegEdBrokerDealerSummary {
	id?: string;
	name?: string;
}

export enum RegEdConfigurationRequirement {
	Unknown = 'Unknown',
	BrokerDealer = 'BrokerDealer',
}

export interface IRegEdConfigurationStatus {
	needsConfiguration?: boolean;
	missingRequirements?: RegEdConfigurationRequirement[];
}

export interface IRegEdIntegrationStatus {
	id?: string;
	brokerDealerId?: string;
	/**
	 * Kill-switch: configured ≠ enabled. The account routes compliance through RegEd only when this is
	 * `true` AND `needsConfiguration` is `false`; only the enable/disable route flips it.
	 */
	enabled?: boolean;
	configurationStatus?: IRegEdConfigurationStatus;
	connectedAt?: string;
	connectedByUserId?: string;
}

export interface IRegEdConnectionTestResult {
	brokerDealerId?: string;
	success?: boolean;
	message?: string;
	testedAt?: string;
}

/** `answers` is keyed by `RegEdMetadataField.id`. */
export interface IRegEdSubmissionMetadata {
	format: string;
	answers: Record<string, unknown>;
}

export const buildRegEdSubmissionMetadata = (
	format: string,
	answers: Record<string, unknown>
): IRegEdSubmissionMetadata => ({ format, answers });

export enum RegEdComplianceContentType {
	Unknown = 'Unknown',
	Email = 'Email',
	Blog = 'Blog',
	SocialMediaPost = 'SocialMediaPost',
}

export interface IRegEdSubmissionCreateRequest {
	parentType: RegEdComplianceContentType;
	parentId: string;
	approvalRequestId: string;
	metadata: IRegEdSubmissionMetadata;
	revisesApprovalRequestId?: string | null;
	/**
	 * Only when the requested review date is inside the firm's notice window and the advisor has
	 * explained why. The reviewer reads it. Sending it otherwise records a rush that never happened.
	 */
	reviewLeadTimeOverrideReason?: string;
}

/**
 * What the compliance prompt hands back. The reason rides alongside the answers so the flows that
 * stash this between the form and the submission carry it without each needing to know about it;
 * `submitRegEdCompliance` splits it back out onto the request body.
 */
export interface IRegEdSubmissionInput extends IRegEdSubmissionMetadata {
	reviewLeadTimeOverrideReason?: string;
	/**
	 * Floors the send date in the schedulers. Already clamped to today or later by the form — a review
	 * date that has passed imposes no floor, and handing a past date to a scheduler lowers its floor
	 * instead of failing to bind. Never reaches the wire.
	 */
	earliestSendDate?: string;
}

/** `category` on the envelope's `error`; unrelated 400s carry no error object at all. */
export const REGED_COMPLIANCE_ERROR_CATEGORY = 'RegEdComplianceErrorCode';

/** Server-owned and append-only — never renumbered. */
export enum RegEdComplianceErrorCode {
	ReviewLeadTimeTooSoon = 1,
	RequestedReviewDateMissing = 2,
	ReviewLeadTimeOverrideReasonTooLong = 3,
}

export const REGED_LEAD_TIME_OVERRIDE_REASON_MAX_LENGTH = 1000;

/** A reason resolves the rejection; a missing review date has to be answered in the questionnaire. */
export const isRegEdLeadTimeReasonFixable = (errorCode: RegEdComplianceErrorCode | undefined): boolean =>
	errorCode === RegEdComplianceErrorCode.ReviewLeadTimeTooSoon ||
	errorCode === RegEdComplianceErrorCode.ReviewLeadTimeOverrideReasonTooLong;

/** The compliance error code on a failed operation result, or undefined when it failed for another reason. */
export const getRegEdComplianceErrorCode = (error: unknown): RegEdComplianceErrorCode | undefined => {
	const technicalError = (error as { error?: { category?: string; errorCode?: number } } | null)?.error;
	return technicalError?.category === REGED_COMPLIANCE_ERROR_CATEGORY
		? (technicalError.errorCode as RegEdComplianceErrorCode)
		: undefined;
};

/** Recorded when a submission was granted inside the notice window; null when the window was met. */
export interface IRegEdReviewLeadTimeOverride {
	reason?: string;
	requestedReviewDate?: string;
	/** Snapshotted, so a historical record still reads correctly if the firm's window changes. */
	requiredLeadTimeDays?: number;
	byUserId?: string;
	at?: string;
}

/** Firm-level content Levitate files on the broker-dealer's behalf, so its advisors needn't file it individually. */
export const REGED_VENDOR_SUBMISSION_FORMAT = 'Vendor Submission';

/**
 * Formats a Levitate admin files on the firm's behalf, from the admin app rather than by an advisor.
 * Presentational only for now — the submissions endpoint accepts any format in the broker-dealer's
 * catalog regardless of caller, so this cannot be the enforcement point.
 */
export const REGED_ADMIN_ONLY_FORMATS = [REGED_VENDOR_SUBMISSION_FORMAT];

export const isRegEdAdvisorSelectableFormat = (format: string): boolean =>
	!REGED_ADMIN_ONLY_FORMATS.some(adminOnly => adminOnly.toLowerCase() === format?.trim().toLowerCase());

export interface IRegEdUserMap {
	id?: string;
	levitateUserId?: string;
	regEdUserId?: string;
}

export interface IRegEdUserMapUpsertRequest {
	regEdUserId: string;
}

/** The UI calls this a Rep Code; the wire field is `regEdUserId`. AdTrax caps it at 24. */
export const REGED_USER_ID_MAX_LENGTH = 24;

export enum RegEdSubmissionStatus {
	Pending = 'Pending',
	Submitting = 'Submitting',
	Approved = 'Approved',
	Rejected = 'Rejected',
	Withdrawn = 'Withdrawn',
	/** Submit failed, no tracking number — needs an operator retry. */
	Failed = 'Failed',
	/** Network failure mid-submit — may or may not have reached AdTrax. */
	Unknown = 'Unknown',
	/** No AdTrax decision within the SLA window. */
	Expired = 'Expired',
}

export enum RegEdApprovalStatus {
	Rejected = 'Rejected',
	Approved = 'Approved',
}

/** AdTrax decision code that gates the revise-and-resubmit flow (see `decisionCode`). */
export const REGED_REVISABLE_DECISION_CODE = 'Revise and Resubmit';

/** Approved, but the piece is HELD until the advisor completes the reviewer's condition and releases it. */
export const REGED_CONDITIONALLY_APPROVED_DECISION_CODE = 'Conditionally Approved';

/** Hard reject — terminal; the revise flow must NOT be offered (the server 400s a revision). */
export const REGED_DECLINED_DECISION_CODE = 'Declined';

/**
 * AdTrax pads some decision codes (observed `"Conditionally Approved "`) and rows cached before the
 * server-side canonicalization keep the padding — there is no backfill, so always compare trimmed and
 * case-insensitively or a historical row silently misses its gate.
 */
export const isRegEdDecisionCode = (decisionCode: string | null | undefined, expected: string): boolean =>
	decisionCode?.trim().toLowerCase() === expected.trim().toLowerCase();

/**
 * Determinate only once a response exists — a pending AR has `response == null` and must read as
 * "unknown", never "released", or a held piece would hide its release action and silently miss its send.
 */
export const getRegEdConditionsPending = (
	approvalRequests: Array<{ response?: { conditionsPending?: boolean } | null }> | null | undefined
): boolean | undefined =>
	approvalRequests?.some(x => !!x.response)
		? approvalRequests.some(x => x.response?.conditionsPending === true)
		: undefined;

/**
 * The round `revise` just raised, resolved by recency among the unanswered rounds rather than index 0.
 * Superseded rounds are filtered out of every response, but a co-approver's own unanswered round stays
 * live and can precede the new one in the array — so position says nothing about which is the revision.
 */
export const findRaisedRegEdRound = <T extends { creationDate?: string; id?: string; response?: unknown }>(
	approvalRequests: T[] | null | undefined
): T | undefined =>
	(approvalRequests ?? [])
		.filter(request => !request.response)
		// Coalesced rather than filtered: a round with no creationDate would otherwise sort by NaN, which
		// is implementation-defined. Sorting it oldest keeps it selectable when it is the only open round,
		// where dropping it would leave the caller with nothing to file against.
		.sort(
			(a, b) =>
				(b.creationDate ? new Date(b.creationDate).valueOf() : 0) -
				(a.creationDate ? new Date(a.creationDate).valueOf() : 0)
		)[0];

/** Reviewer's actionable text for a decided submission — the condition to meet or the reason to revise. */
export interface IRegEdReviewNote {
	noteText?: string;
	addedByName?: string;
	addedByEmail?: string;
	addedOn?: string | null;
}

/**
 * Why a decided piece is back with the advisor. Resolved server-side from the broker-dealer's
 * decision-code configuration, so switch on this rather than reconstructing intent from the content
 * status — that differs per type (email holds as `WaitingForApproval`, blog/post as `Pending`, and a
 * revision request looks identical to a hard reject). A hard reject never appears here.
 */
export enum RegEdActionReason {
	RevisionRequested = 'RevisionRequested',
	ConditionsPending = 'ConditionsPending',
}

/** Which email report a send appears under. Absent for blogs and social posts. */
export enum RegEdEmailKind {
	Individual = 'Individual',
	Campaign = 'Campaign',
	Automation = 'Automation',
}

/** One piece whose compliance decision landed but that still owes the advisor something. */
export interface IRegEdActionNeededItem {
	reason?: RegEdActionReason;
	/** Names the tab the piece is under, so a link can land on the list that actually holds it. */
	emailKind?: RegEdEmailKind | null;
	contentType?: RegEdComplianceContentType;
	parentId?: string;
	approvalRequestId?: string;
	submissionId?: string;
	trackingNumber?: string;
	/** Raw AdTrax code, for display only — `reason` is the field to branch on. */
	decisionCode?: string;
	title?: string;
	/** The piece's own send date; routinely in the past, since a decision can land after it. */
	scheduledDate?: string | null;
	reviewNotes?: IRegEdReviewNote[];
}

export interface IRegEdSubmissionAnnotation {
	id?: string;
	fileName?: string;
	mimeType?: string;
}

/** Rows are submission *chains* (a revision repoints the same row), returned newest-first. */
export interface IRegEdSubmissionView {
	id?: string;
	creationDate?: string;
	lastModifiedDate?: string;
	approvalRequestId?: string;
	revisesApprovalRequestId?: string | null;
	contentType?: RegEdComplianceContentType;
	parentId?: string;
	brokerDealerId?: string;
	status?: RegEdSubmissionStatus;
	approvalStatus?: RegEdApprovalStatus | null;
	trackingNumber?: string | null;
	decisionCode?: string | null;
	metadata?: IRegEdSubmissionMetadata;
	/** Absent until the API exposes them; `[]` when the reviewer left none. */
	reviewNotes?: IRegEdReviewNote[];
	/** Reviewer PDFs for a decided submission; `[]` when none exist. */
	annotations?: IRegEdSubmissionAnnotation[];
	/** Present only when the submission was granted inside the firm's notice window. */
	reviewLeadTimeOverride?: IRegEdReviewLeadTimeOverride | null;
}
